import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@tanstack/react-query';
import type { ExecutiveSummaryCitation } from '@trinity/chat/types/segment-executive-summary';
import { getSegmentAggregatedData } from '../api/client';
import CitationRunRenderer from './citationRun/CitationRunRenderer';
import ExecutiveSummaryProgress from './ExecutiveSummaryProgress';

interface ExecutiveSummaryProps {
  segmentId: number;
}

export default function ExecutiveSummary({ segmentId }: ExecutiveSummaryProps): React.JSX.Element | null {
  const [citationsOpen, setCitationsOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['segment-aggregated-data', segmentId],
    queryFn: () => getSegmentAggregatedData(segmentId),
    refetchInterval: (query) =>
      query.state.data?.item?.executiveSummaryStatus === 'generating' ? 5000 : false,
  });

  const item = data?.item;
  const payload = item?.executiveSummary ?? null;
  const status = item?.executiveSummaryStatus;

  const handleCitationClick = useCallback((number: number) => {
    setCitationsOpen(true);
    // Defer until the citation list has rendered; double rAF covers the
    // case where `open` was false and the CitationEntry DOM node only
    // exists after the next paint.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`exec-cite-${number}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, []);

  if (!isLoading && item && status === 'stale' && !payload) {
    return (
      <Root>
        <Header>
          <span>Summary</span>
        </Header>
        <Empty>Executive summary not yet generated.</Empty>
      </Root>
    );
  }

  // Completion handshake: when the backend flips out of "generating" and
  // the payload arrives, hold the spinner on-screen until it tweens to
  // 100%. `wasGenerating` tracks whether we've actually shown the spinner
  // this session so a cold-load with existing data skips the tween.
  const isServerGenerating = isLoading || status === 'generating';
  const wasGeneratingRef = useRef(false);
  const [tweenFinished, setTweenFinished] = useState(false);
  useEffect(() => {
    if (isServerGenerating) {
      wasGeneratingRef.current = true;
      setTweenFinished(false);
    }
  }, [isServerGenerating]);

  const completing = wasGeneratingRef.current && !isServerGenerating && !tweenFinished && !!payload;
  const showGenerating = isServerGenerating || completing;

  return (
    <Root>
      <Header>
        <span>Summary</span>
      </Header>
      <Content>
        {showGenerating ? (
          <LoadingContainer>
            <ExecutiveSummaryProgress
              completing={completing}
              onCompletionFinished={() => setTweenFinished(true)}
            />
            <LoadingText>Generating executive summary&hellip;</LoadingText>
          </LoadingContainer>
        ) : payload ? (
          <>
            <BodyWrap>
              <CitationRunRenderer
                body={payload.body_markdown}
                citations={payload.citations}
                onCitationClick={handleCitationClick}
              />
            </BodyWrap>
            <CitationsFooter>
              <CitationsHeader onClick={() => setCitationsOpen((v) => !v)}>
                <span>Sources ({payload.citations.length})</span>
                <ExpandIcon $expanded={citationsOpen}>
                  <ExpandMoreIcon fontSize="small" />
                </ExpandIcon>
              </CitationsHeader>
              {citationsOpen && (
                <CitationsList>
                  {payload.citations.map((citation) => (
                    <CitationCard key={citation.number} id={`exec-cite-${citation.number}`}>
                      <CitationHeader>
                        <CitationNumber>[{citation.number}]</CitationNumber>
                        <CitationSourceLink href={citation.anchor}>
                          {citation.source_title}
                        </CitationSourceLink>
                        <CitationTypeBadge $type={citation.source_type}>
                          {citation.source_type}
                        </CitationTypeBadge>
                      </CitationHeader>
                      {citation.source_subtitle && (
                        <CitationSubtitle>{citation.source_subtitle}</CitationSubtitle>
                      )}
                      {citation.data_points.length > 0 &&
                        (citation.source_type === 'chart' ? (
                          <ChipRow>
                            {citation.data_points.map((point, i) => (
                              <Chip key={i} $primary={point.emphasis === 'primary'}>
                                <ChipLabel>{point.key}</ChipLabel>
                                <ChipValue>
                                  {typeof point.value === 'number'
                                    ? formatNumericValue(point.value, point.unit)
                                    : String(point.value)}
                                </ChipValue>
                              </Chip>
                            ))}
                            {citation.truncated_count ? (
                              <MoreIndicator>+{citation.truncated_count} more</MoreIndicator>
                            ) : null}
                          </ChipRow>
                        ) : (
                          <KeyValueGrid>
                            {citation.data_points.map((point, i) => (
                              <KeyValueRow key={i} $primary={point.emphasis === 'primary'}>
                                <KeyCell>{point.key}</KeyCell>
                                <ValueCell>
                                  {String(point.value)}
                                  {point.unit ? ` ${point.unit}` : ''}
                                </ValueCell>
                              </KeyValueRow>
                            ))}
                            {citation.truncated_count ? (
                              <MoreIndicator>+{citation.truncated_count} more</MoreIndicator>
                            ) : null}
                          </KeyValueGrid>
                        ))}
                    </CitationCard>
                  ))}
                </CitationsList>
              )}
            </CitationsFooter>
          </>
        ) : null}
      </Content>
    </Root>
  );
}

function formatNumericValue(value: number, unit: string | undefined): string {
  if (unit === '%') {
    return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
  }
  return unit ? `${value} ${unit}` : String(value);
}

export type { ExecutiveSummaryCitation };

const Root = styled.div`
  margin-bottom: 12px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  > span {
    font-size: 16px;
    font-weight: 600;
    color: var(--black-l, #323232);
  }
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
`;

const Content = styled.div``;

const Empty = styled.div`
  padding: 16px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  font-style: italic;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
`;

const LoadingText = styled.span`
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
`;

const BodyWrap = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: var(--black-l, #323232);

  a[href^='#exec-cite-'] {
    display: inline-block;
    padding: 1px 3px;
    margin: 0 1px;
    border-radius: 3px;
    background: #dbe4f5;
    color: #4a6bb0;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.4;
    text-decoration: none;
    vertical-align: baseline;
    &:hover {
      background: #c3d2ee;
    }
  }

  h3 {
    font-size: 15px;
    margin-top: 16px;
    margin-bottom: 6px;
  }

  p {
    margin: 0 0 10px;
  }
`;

const CitationsFooter = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const CitationsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
`;

const CitationsList = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CitationCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  font-size: 12px;
  scroll-margin-top: 220px;
  scroll-margin-bottom: 16px;

  &:target {
    border-color: #4a6bb0;
    box-shadow: 0 0 0 2px rgba(74, 107, 176, 0.12);
  }
`;

const CitationHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const CitationNumber = styled.span`
  color: #4a6bb0;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
`;

const CitationSourceLink = styled.a`
  color: var(--black-l, #323232);
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const CitationTypeBadge = styled.span<{ $type: 'card' | 'chart' }>`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${(p) => (p.$type === 'chart' ? '#e4ecf7' : '#f0ebf7')};
  color: ${(p) => (p.$type === 'chart' ? '#2f548c' : '#6a4c93')};
  flex-shrink: 0;
`;

const CitationSubtitle = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.6);
`;

const KeyValueGrid = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
  row-gap: 4px;
  font-size: 12px;
`;

const KeyValueRow = styled.div<{ $primary?: boolean }>`
  display: contents;
  & > :first-of-type {
    color: rgba(0, 0, 0, 0.55);
    font-weight: ${(p) => (p.$primary ? 600 : 500)};
  }
  & > :last-of-type {
    color: var(--black-l, #323232);
    font-weight: ${(p) => (p.$primary ? 600 : 400)};
  }
`;

const KeyCell = styled.div`
  line-height: 1.5;
`;

const ValueCell = styled.div`
  line-height: 1.5;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.div<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${(p) => (p.$primary ? '#dbe4f5' : '#f1f4fa')};
  border: 1px solid ${(p) => (p.$primary ? '#bccbe8' : 'rgba(0, 0, 0, 0.04)')};
  font-size: 11px;
`;

const ChipLabel = styled.span`
  color: rgba(0, 0, 0, 0.6);
`;

const ChipValue = styled.span`
  color: #4a6bb0;
  font-weight: 600;
`;

const MoreIndicator = styled.div`
  font-size: 11px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.45);
  grid-column: 1 / -1;
`;
